/**
 * Booking subsystem API client (F-131).
 *
 * Wraps `/me/working-hours/`, `/me/blocked-windows/`, public slots,
 * order hold/release, and calendar token endpoints.
 */
import { api } from './client';

export interface WorkingHours {
  id: number;
  weekday: number; // 0=Mon..6=Sun
  start_time: string; // "09:00:00"
  end_time: string;
  is_active: boolean;
}

export interface WorkingHoursInput {
  weekday: number;
  start_time: string;
  end_time: string;
  is_active?: boolean;
}

export async function fetchWorkingHours(): Promise<{ results: WorkingHours[] }> {
  return api.get('/me/working-hours/');
}

export async function replaceWorkingHours(
  items: WorkingHoursInput[],
): Promise<{ results: WorkingHours[] }> {
  return api.put('/me/working-hours/', { body: { items } });
}

export interface BlockedWindow {
  id: number;
  start_at: string;
  end_at: string;
  reason: string;
}

export interface BlockedWindowInput {
  start_at: string;
  end_at: string;
  reason?: string;
}

export async function fetchBlockedWindows(): Promise<{ results: BlockedWindow[] }> {
  return api.get('/me/blocked-windows/');
}

export async function createBlockedWindow(
  input: BlockedWindowInput,
): Promise<BlockedWindow> {
  return api.post('/me/blocked-windows/', { body: input });
}

export async function deleteBlockedWindow(id: number): Promise<void> {
  await api.del(`/me/blocked-windows/${id}/`);
}

export interface Slot {
  id: number;
  start_at: string;
  duration_min: number;
  status: 'free' | 'held' | 'booked';
}

export async function fetchSpecialistSlots(
  slug: string,
  range: { from?: string; to?: string } = {},
): Promise<{ results: Slot[] }> {
  return api.get(`/specialists/${slug}/slots/`, {
    query: { from: range.from, to: range.to },
  });
}

export interface HoldResponse {
  slot_id: number;
  held_until: string;
  status: string;
}

export async function holdSlot(orderId: number, slotId: number): Promise<HoldResponse> {
  return api.post(`/orders/${orderId}/hold-slot/`, { body: { slot_id: slotId } });
}

export async function releaseHold(orderId: number, slotId: number): Promise<void> {
  await api.post(`/orders/${orderId}/release-hold/`, { body: { slot_id: slotId } });
}

export interface CalendarTokenResponse {
  token: string;
  url: string;
  last_used_at: string | null;
}

export async function getCalendarToken(): Promise<CalendarTokenResponse> {
  return api.get('/me/calendar/token/');
}

export async function rotateCalendarToken(): Promise<CalendarTokenResponse> {
  return api.post('/me/calendar/rotate-token/');
}
